function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { namedNode } from '@rdf-esm/data-model';
import TermMap from '@rdf-esm/term-map';
import cf from 'clownface';
import TermSet from '@rdf-esm/term-set';
import { hydra } from '@tpluscode/rdf-ns-builders';
import fromStream from 'rdf-dataset-ext/fromStream';
import FetchUtil from "./FetchUtil.mjs";
import { merge } from "./helpers/MergeHeaders.mjs";
import { getAbsoluteUri } from "./helpers/uri.mjs";
import * as DefaultCacheStrategy from "./ResourceCacheStrategy.mjs";

function byInProperties(left, right) {
  return left.in().terms.length - right.in().terms.length;
}

export class Alcaeus {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor(init) {
    _defineProperty(this, "baseUri", undefined);

    _defineProperty(this, "rootSelectors", void 0);

    _defineProperty(this, "parsers", void 0);

    _defineProperty(this, "defaultHeaders", {});

    _defineProperty(this, "defaultRequestInit", {});

    _defineProperty(this, "log", () => {});

    _defineProperty(this, "cacheStrategy", { ...DefaultCacheStrategy
    });

    _defineProperty(this, "resources", void 0);

    _defineProperty(this, "__apiDocumentations", new TermMap());

    _defineProperty(this, "datasetFactory", void 0);

    _defineProperty(this, "_fetch", void 0);

    _defineProperty(this, "_headers", void 0);

    const {
      resources,
      datasetFactory,
      rootSelectors,
      parsers,
      fetch
    } = init;
    if (!parsers) throw new Error('No parsers provided. Consider @rdfjs/formats-common or @rdf-esm/formats-common packages');
    this.rootSelectors = rootSelectors;
    this.resources = resources;
    this.datasetFactory = datasetFactory;
    this.parsers = parsers;
    this._headers = init.Headers;
    this._fetch = FetchUtil(fetch, this._headers);
  }

  get apiDocumentations() {
    return [...this.__apiDocumentations.values()];
  }

  async loadResource(id, headers = {}, requestInit, dereferenceApiDocumentation = true) {
    const term = typeof id === 'string' ? namedNode(id) : id;
    let requestHeaders = new this._headers(headers);
    const previousResource = this.resources.get(term);

    if (previousResource) {
      if (!this.cacheStrategy.shouldLoad(previousResource)) {
        return previousResource;
      }

      const cacheHeadersInit = this.cacheStrategy.requestCacheHeaders(previousResource);

      if (cacheHeadersInit) {
        const cacheHeaders = new this._headers(cacheHeadersInit);
        requestHeaders = merge(requestHeaders, cacheHeaders, this._headers);
      }
    }

    const uri = getAbsoluteUri(term.value, this.baseUri);
    const defaultRequestInit = typeof this.defaultRequestInit === 'function' ? await this.defaultRequestInit({
      uri
    }) : this.defaultRequestInit;
    const response = await this._fetch.resource(uri, {
      parsers: this.parsers,
      headers: await this.__mergeHeaders(requestHeaders, {
        uri
      }),
      ...{ ...defaultRequestInit,
        ...requestInit
      }
    });

    if (previousResource) {
      const etag = response.xhr.headers.get('etag');
      const previousEtag = previousResource.response.xhr.headers.get('etag');
      const etagsEqual = etag && etag === previousEtag;

      if (response.xhr.status === 304 || etagsEqual) {
        return previousResource;
      }
    }

    const stream = await response.quadStream();

    if (stream) {
      const dataset = await fromStream(this.datasetFactory(), stream);

      const rootResource = this.__findRootResource(dataset, response);

      if (dereferenceApiDocumentation) {
        await this.__getApiDocumentation(response, headers);
      }

      const resources = response.xhr.ok ? this.resources : this.resources.clone();
      await resources.set(term, {
        response,
        dataset,
        rootResource
      });
      return resources.get(term);
    }

    return {
      response
    };
  }

  async loadDocumentation(id, headers = {}, requestInit = {}) {
    const term = typeof id === 'string' ? namedNode(id) : id;
    const resource = await this.loadResource(term, headers, requestInit, false);

    if (resource && resource.representation) {
      this.__apiDocumentations.set(term, resource.representation);

      const [apiDocs] = resource.representation.ofType(hydra.ApiDocumentation);

      if (apiDocs) {
        return apiDocs;
      }
    }

    return null;
  }

  async invokeOperation(operation, headers = {}, body, requestInit = {}) {
    const uri = getAbsoluteUri(operation.target.id.value, this.baseUri);
    const mergedHeaders = await this.__mergeHeaders(new this._headers(headers), {
      uri
    });

    if (!operation.method) {
      throw new Error('Cannot invoke operation without a hydra:method');
    }

    const defaultRequestInit = typeof this.defaultRequestInit === 'function' ? await this.defaultRequestInit({
      uri
    }) : this.defaultRequestInit;
    const response = await this._fetch.operation(operation.method, uri, {
      parsers: this.parsers,
      headers: mergedHeaders,
      body,
      ...{ ...defaultRequestInit,
        ...requestInit
      }
    });
    await this.__getApiDocumentation(response, headers);
    const responseStream = await response.quadStream();

    if (responseStream) {
      var _operation$method;

      const dataset = await fromStream(this.datasetFactory(), responseStream);

      const rootResource = this.__findRootResource(dataset, response);

      let resources = this.resources;

      if (((_operation$method = operation.method) === null || _operation$method === void 0 ? void 0 : _operation$method.toUpperCase()) !== 'GET') {
        resources = this.resources.clone();
      }

      await resources.set(namedNode(response.resourceUri), {
        dataset,
        rootResource,
        response
      });
      return resources.get(namedNode(response.resourceUri));
    }

    return {
      response
    };
  }

  async __getApiDocumentation(response, headers) {
    if (!response.apiDocumentationLink) {
      this.log(`Resource ${response.requestedUri} does not expose API Documentation link`);
      return;
    }

    await this.loadDocumentation(response.apiDocumentationLink, headers);
  }

  async __mergeHeaders(headers, {
    uri
  }) {
    const defaultHeaders = typeof this.defaultHeaders === 'function' ? await this.defaultHeaders({
      uri
    }) : this.defaultHeaders;
    return merge(new this._headers(defaultHeaders), headers, this._headers);
  }

  __findRootResource(dataset, response) {
    const candidateNodes = this.rootSelectors.reduce((candidates, [, getCandidate]) => {
      const candidate = getCandidate(response, dataset);

      if (candidate && dataset.match(candidate).size) {
        candidates.add(candidate);
      }

      return candidates;
    }, new TermSet());

    if (!candidateNodes.size) {
      return undefined;
    }

    const candidates = [...candidateNodes].map(term => cf({
      dataset,
      term
    }));
    return candidates.sort(byInProperties)[0].term;
  }

}